import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import {
  getCategorieSettings,
  updateCategoriaSettings,
  type CategoriaSettings,
  type TipoOperazione,
} from '../../api/settings';
import { useAuthStore } from '../../store/authStore';
import { canWriteSettings, canWriteIvaDefault } from '../../lib/constants';

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white disabled:bg-stone-bg disabled:text-ink-soft disabled:cursor-not-allowed disabled:border-stone-mid';

// Bozza editabile per riga: prezzo e IVA come stringa (input), direzione e attiva.
interface Draft {
  tariffa: string;
  tipo_operazione_default: TipoOperazione;
  iva_default: string;
  attiva: boolean;
}

function toDraft(c: CategoriaSettings): Draft {
  return {
    tariffa: c.tariffa_eur_kg != null ? String(c.tariffa_eur_kg) : '',
    tipo_operazione_default: c.tipo_operazione_default,
    iva_default: String(c.iva_default),
    attiva: c.attiva,
  };
}

export default function CategorieSettings() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  // Scrittura Settings = {admin, DIR, RT}; AMM sola lettura.
  const canWrite = canWriteSettings(role);
  // L'IVA di default è un parametro fiscale: editabile solo da {admin, DIR}.
  const canWriteIva = canWriteIvaDefault(role);
  const [items, setItems] = useState<CategoriaSettings[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try {
        const data = await getCategorieSettings();
        setItems(data);
        setDrafts(Object.fromEntries(data.map((c) => [c.categoria, toDraft(c)])));
      } catch {
        setError(t('settings.categorie.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);
  useEffect(() => { reload(); }, [reload]);

  // Il badge "salvato" si spegne da solo dopo 3s. Il timer sta QUI e non nel
  // gestore di `save()`: li' era un `setTimeout` senza `clearTimeout`, e dopo
  // lo smontaggio scattava lo stesso chiamando setState su un componente che
  // non c'e' piu' — vitest usciva con 1 anche a test tutti verdi, in modo
  // dipendente dal carico. Stessa forma del toast di DettaglioRichiestaPage.
  // Nota: risalvare la STESSA riga entro i 3s non riarma il timer (lo stato
  // non cambia, l'effetto non rigira), quindi il badge sparisce 3s dopo il
  // primo salvataggio. E' un badge, non un'informazione che si perde.
  useEffect(() => {
    if (!savedKey) return;
    const timer = setTimeout(() => setSavedKey(null), 3000);
    return () => clearTimeout(timer);
  }, [savedKey]);

  function patchDraft(categoria: string, patch: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [categoria]: { ...d[categoria]!, ...patch } }));
  }

  async function save(c: CategoriaSettings) {
    const d = drafts[c.categoria]!;
    const tariffa = d.tariffa.trim() === '' ? undefined : Number(d.tariffa);
    if (tariffa != null && (Number.isNaN(tariffa) || tariffa < 0)) {
      setError(t('settings.categorie.val_prezzo'));
      return;
    }
    // iva_default solo per {admin, DIR}: per RT il body NON la include (no 403/422).
    let iva: number | undefined;
    if (canWriteIva) {
      iva = Number(d.iva_default);
      if (Number.isNaN(iva) || iva <= 0 || iva > 100) {
        setError(t('settings.categorie.val_iva'));
        return;
      }
    }
    setSavingKey(c.categoria); setError(null); setSavedKey(null);
    try {
      const updated = await updateCategoriaSettings(c.categoria, {
        tariffa_eur_kg: tariffa,
        tipo_operazione_default: d.tipo_operazione_default,
        iva_default: iva,
        attiva: d.attiva,
      });
      setItems((list) => list.map((x) => (x.categoria === c.categoria ? updated : x)));
      setDrafts((dd) => ({ ...dd, [c.categoria]: toDraft(updated) }));
      setSavedKey(c.categoria);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? t('settings.categorie.errore_salvataggio'));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="space-y-5 bg-white border border-stone-mid rounded-xl p-5">
      <p className="text-sm text-ink-mid">
        {t('settings.categorie.intro')}
        <span className="text-ink-soft"> {t('settings.categorie.intro_dettaglio')}</span>
      </p>

      {error && <div className="p-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-6 text-sm text-ink-soft">{t('settings.categorie.caricamento')}</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-mid text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="py-2">{t('settings.categorie.col_categoria')}</th>
              <th className="py-2">{t('settings.categorie.col_prezzo')}</th>
              <th className="py-2">{t('settings.categorie.col_iva')}</th>
              <th className="py-2">{t('settings.categorie.col_direzione')}</th>
              <th className="py-2">{t('settings.categorie.col_attiva')}</th>
              {canWrite && <th className="py-2 text-right">{t('settings.categorie.col_azioni')}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const d = drafts[c.categoria]!;
              return (
                <tr key={c.categoria} className="border-b border-stone-mid/60" data-testid={`categoria-row-${c.categoria}`}>
                  <td className="py-2 font-medium text-ink-dark">{c.label}</td>
                  <td className="py-2 pr-3">
                    <input
                      type="number" step="0.01" min="0" className={inputCls}
                      data-testid={`categoria-prezzo-${c.categoria}`}
                      value={d.tariffa}
                      disabled={!canWrite}
                      onChange={(e) => patchDraft(c.categoria, { tariffa: e.target.value })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="number" step="0.01" min="0" max="100" className={inputCls}
                      data-testid={`categoria-iva-${c.categoria}`}
                      value={d.iva_default}
                      disabled={!canWriteIva}
                      onChange={(e) => patchDraft(c.categoria, { iva_default: e.target.value })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      className={inputCls}
                      data-testid={`categoria-direzione-${c.categoria}`}
                      value={d.tipo_operazione_default}
                      disabled={!canWrite}
                      onChange={(e) => patchDraft(c.categoria, { tipo_operazione_default: e.target.value as TipoOperazione })}
                    >
                      <option value="servizio">{t('settings.categorie.dir_servizio')}</option>
                      <option value="acquisto">{t('settings.categorie.dir_acquisto')}</option>
                    </select>
                  </td>
                  <td className="py-2">
                    <input
                      type="checkbox"
                      data-testid={`categoria-attiva-${c.categoria}`}
                      checked={d.attiva}
                      disabled={!canWrite}
                      onChange={(e) => patchDraft(c.categoria, { attiva: e.target.checked })}
                      className="h-4 w-4 accent-sage"
                    />
                  </td>
                  {canWrite && (
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => save(c)}
                        disabled={savingKey === c.categoria}
                        data-testid={`categoria-save-${c.categoria}`}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60 inline-flex items-center gap-1"
                      >
                        {savedKey === c.categoria ? <><Check size={13} /> {t('settings.categorie.salvato')}</> : savingKey === c.categoria ? t('settings.categorie.salvataggio') : t('settings.categorie.salva')}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
